import { useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import {
  MINES_COUNT,
  MINES_GRID,
  MINES_MAX_PICKS,
  minesMultAfter,
  minesSurvival,
} from "@karrtopelka/game-api";
import { fromScoreValue } from "@karrtopelka/game-api/crash";
import type { CardProps } from "@karrtopelka/cards/types";

import { buildMinesBustCard, buildMinesCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { cashoutRound, revealTile, startRound } from "./lib/minesApi.js";
import { useMinesStore } from "./store.js";

type Board = ReturnType<typeof useLeaderboard>;

export function App() {
  const phase = useMinesStore((s) => s.phase);

  // One leaderboard instance feeds both the desktop rail and the mobile board.
  const board = useLeaderboard("mines");
  const [handle, setHandleState] = useState<string | null>(() => getHandle());

  // Warm the card-render assets on first play intent.
  const warmed = useRef(false);
  useEffect(() => {
    if (!warmed.current && phase !== "idle") {
      warmed.current = true;
      void loadCardAssets().catch(() => {});
    }
  }, [phase]);

  // Funnel beacons.
  useEffect(() => trackPageView("mines"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "playing" && !started.current) {
      started.current = true;
      track("mines", "play_start");
    }
    if (phase === "busted" || phase === "cashed") track("mines", "play_done");
  }, [phase]);

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          mines
          <span className="animate-glow text-accent">.</span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          tap gems. dodge {MINES_COUNT} mines. cash out before the boom.
        </p>
      </header>

      <LadderRail />

      <Game board={board} handle={handle} onHandle={setHandleState} />

      <aside
        className="sticky top-8 hidden animate-fade-up flex-col gap-3 lg:flex"
        style={{ animationDelay: "160ms" }}
      >
        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-text-muted">
            best sweeps
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={handle}
            formatValue={(v) => `${fromScoreValue(v)}x`}
            emptyLabel="minefield's untouched — sweep it."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-6"
        style={{ animationDelay: "240ms" }}
      >
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://crash.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try crash — cash out before it crashes →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>
        <div className="hidden flex-col items-center gap-3 lg:flex">
          <span className="uppercase tracking-wide">more games</span>
          <div className="flex items-stretch gap-3">
            <GameChip
              href="https://crash.karrtopelka.com"
              name="crash"
              hook="cash out or crash"
            />
            <GameChip
              href="https://plinko.karrtopelka.com"
              name="plinko"
              hook="one ball, 470x"
            />
            <GameChip
              href="https://limbo.karrtopelka.com"
              name="limbo"
              hook="name your multiplier"
            />
          </div>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com — all games
          </a>
        </div>
      </footer>
    </main>
  );
}

function GameChip({
  href,
  name,
  hook,
}: {
  href: string;
  name: string;
  hook: string;
}) {
  return (
    <a
      href={href}
      className="flex flex-col items-center rounded-lg border border-border bg-surface/60 px-5 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-[0_0_24px_rgba(255,111,165,0.2)]"
    >
      <span className="font-display text-sm font-bold text-text">
        {name}
        <span className="text-accent">.</span>
      </span>
      <span className="text-[11px] text-text-faint">{hook}</span>
    </a>
  );
}

/**
 * Desktop-only left rail: how it works, the full payout ladder (the whole
 * greed curve, current rung lit), your best, and the keyboard hint.
 */
function LadderRail() {
  const phase = useMinesStore((s) => s.phase);
  const gems = useMinesStore((s) => s.picks.length);
  const best = useMinesStore((s) => s.best);
  const playing = phase === "playing";

  return (
    <aside
      className="sticky top-8 hidden animate-fade-up flex-col gap-4 lg:flex"
      style={{ animationDelay: "80ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="mt-2 flex flex-col gap-1 text-xs text-text-faint">
          <li>
            {MINES_GRID} tiles. {MINES_COUNT} are mines.
          </li>
          <li>every gem multiplies the pot.</li>
          <li>cash out before the boom.</li>
        </ul>
        {best > 0 && (
          <p className="mt-3 border-t border-border pt-2 text-xs text-text-faint">
            your best{" "}
            <span className="font-display text-sm font-bold text-accent">
              {fromScoreValue(best)}x
            </span>
          </p>
        )}
        <p className="mt-3 text-[11px] text-text-faint">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
            space
          </kbd>{" "}
          — new board / cash out
        </p>
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          payout ladder
        </h2>
        <ol className="mt-2 flex flex-col gap-px">
          {Array.from({ length: MINES_MAX_PICKS }, (_, i) => i + 1).map(
            (k) => {
              const current = playing && gems === k;
              const next = playing && gems + 1 === k;
              const banked = playing && k < gems;
              return (
                <li
                  key={k}
                  className={`flex items-baseline justify-between rounded px-1.5 py-0.5 text-[11px] leading-4 transition-colors duration-200 ${
                    current
                      ? "bg-win/10 font-bold text-win"
                      : next
                        ? "animate-breathe text-accent"
                        : banked
                          ? "text-text-muted"
                          : "text-text-faint"
                  }`}
                  style={
                    current
                      ? { textShadow: "0 0 14px rgba(0,232,123,0.6)" }
                      : undefined
                  }
                >
                  <span>
                    {k} <span className="opacity-70">💎</span>
                  </span>
                  <span className="font-display">
                    {minesMultAfter(k).toFixed(2)}x
                  </span>
                </li>
              );
            },
          )}
        </ol>
      </div>
    </aside>
  );
}

function Game({
  board,
  handle,
  onHandle,
}: {
  board: Board;
  handle: string | null;
  onHandle: (h: string) => void;
}) {
  const phase = useMinesStore((s) => s.phase);
  const roundId = useMinesStore((s) => s.roundId);
  const picks = useMinesStore((s) => s.picks);
  const mult = useMinesStore((s) => s.mult);
  const mines = useMinesStore((s) => s.mines);
  const fatalPick = useMinesStore((s) => s.fatalPick);
  const cashResult = useMinesStore((s) => s.cashResult);
  const best = useMinesStore((s) => s.best);
  const begin = useMinesStore((s) => s.begin);
  const confirmReveal = useMinesStore((s) => s.confirmReveal);
  const bust = useMinesStore((s) => s.bust);
  const cashed = useMinesStore((s) => s.cashed);
  const reset = useMinesStore((s) => s.reset);

  const [showHandle, setShowHandle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);

  const launch = async () => {
    setError(null);
    setCard(null);
    setBusy(true);
    const id = await startRound();
    setBusy(false);
    if (!id) {
      setError("couldn't start — try again");
      return;
    }
    haptic.tap();
    begin(id);
  };

  // Handle BEFORE the round (cashout mustn't stop to ask).
  const onStart = () => {
    if (hasHandle()) void launch();
    else setShowHandle(true);
  };

  const onTile = async (i: number) => {
    if (phase !== "playing" || busy || !roundId || picks.includes(i)) return;
    setBusy(true);
    const res = await revealTile(roundId, i);
    setBusy(false);
    if (!res) {
      setError("network hiccup — tap it again");
      return;
    }
    setError(null);
    if (res.hit) {
      haptic.tooSoon();
      bust(i, res.mines);
      setCard(buildMinesBustCard(picks.length, getHandle() ?? "you"));
      return;
    }
    haptic.round();
    confirmReveal(i, res.mult);
    if (res.cleared) void doCashout(); // swept the whole board — auto-bank it
  };

  const doCashout = async () => {
    if (!roundId || busy) return;
    setBusy(true);
    const res = await cashoutRound(roundId);
    setBusy(false);
    if (!res) {
      setError("cashout failed — tap again");
      return;
    }
    haptic.newBest();
    cashed(res);
    setCard(
      buildMinesCard(res.mult, res.value, getHandle() ?? "you", {
        total: res.total,
        percentile: res.percentile,
      }),
    );
    void board.refresh();
  };

  const gems = picks.length;
  const nextMult = minesMultAfter(gems + 1);
  const survival = Math.round(minesSurvival(gems) * 100);
  const playing = phase === "playing";

  // Desktop nicety: space starts a board / cashes out. Ignored whenever an
  // interactive element has focus so it can never double-fire a tile press.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat) return;
      const t = e.target;
      if (t instanceof HTMLElement && t.closest("button, input, textarea, a"))
        return;
      if (busy) return;
      if (phase === "idle" && !showHandle) {
        e.preventDefault();
        onStart();
      } else if (phase === "playing" && picks.length > 0) {
        e.preventDefault();
        void doCashout();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-4 lg:max-w-xl lg:justify-self-center"
      style={{ animationDelay: "120ms" }}
    >
      {/* the board — the glow warms up the deeper you dig */}
      <div
        className={`grid w-full grid-cols-5 gap-1.5 rounded-lg border p-2 transition-shadow duration-500 lg:gap-2 lg:p-3 ${
          phase === "busted"
            ? "animate-flash-bust border-danger/50"
            : phase === "cashed"
              ? "animate-flash-win border-win/40"
              : "border-border"
        }`}
        style={
          playing && gems > 0
            ? {
                boxShadow: `0 0 ${16 + gems * 4}px rgba(255,111,165,${Math.min(
                  0.1 + gems * 0.03,
                  0.45,
                )})`,
              }
            : undefined
        }
      >
        {Array.from({ length: MINES_GRID }, (_, i) => {
          const revealed = picks.includes(i);
          const isMine = mines?.includes(i) ?? false;
          const fatal = fatalPick === i;
          const over = phase === "busted" || phase === "cashed";
          return (
            <button
              key={i}
              type="button"
              disabled={!playing || busy || revealed}
              onClick={() => void onTile(i)}
              className={`flex aspect-square items-center justify-center rounded-md border text-xl transition-all duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:text-3xl ${
                fatal
                  ? "animate-shake border-danger bg-danger/25"
                  : isMine
                    ? "border-danger/50 bg-danger/10"
                    : revealed
                      ? "animate-pop border-win/50 bg-win/10"
                      : over
                        ? "border-border bg-surface opacity-50"
                        : "border-border bg-surface-raised enabled:hover:-translate-y-0.5 enabled:hover:border-accent enabled:hover:shadow-[0_0_18px_rgba(255,111,165,0.25)]"
              }`}
            >
              {fatal ? "💥" : isMine ? "💣" : revealed ? "💎" : ""}
            </button>
          );
        })}
      </div>

      {/* the ledger line */}
      {playing && (
        <div className="flex w-full items-center justify-between text-sm">
          <span className="font-display text-2xl font-extrabold text-win lg:text-3xl">
            {mult.toFixed(2)}x
          </span>
          <span className="text-xs text-text-faint lg:text-sm">
            next gem →{" "}
            <span className="font-mono text-text-muted">
              {nextMult.toFixed(2)}x
            </span>
            {" · "}
            <span className="font-mono text-text-muted">{survival}%</span> safe
          </span>
        </div>
      )}

      {error && <p className="text-sm text-accent">{error}</p>}

      {/* actions */}
      {phase === "idle" && (
        <button
          type="button"
          onClick={onStart}
          disabled={busy}
          className="w-full animate-neon-pulse rounded-md bg-accent px-4 py-4 text-lg font-bold text-bg transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99] disabled:animate-none disabled:opacity-40"
        >
          {busy ? "dealing…" : "new board"}
        </button>
      )}
      {phase === "idle" && best > 0 && (
        <span className="text-xs text-text-faint">
          your best:{" "}
          <span className="font-display font-bold text-accent">
            {fromScoreValue(best)}x
          </span>
        </span>
      )}

      {playing && (
        <button
          type="button"
          onClick={() => void doCashout()}
          disabled={busy || gems === 0}
          className={`w-full rounded-md px-4 py-4 text-lg font-bold transition-all duration-200 active:scale-[0.98] disabled:opacity-30 ${
            gems > 0
              ? "bg-win text-bg shadow-glow-win hover:-translate-y-0.5"
              : "border border-border text-text-faint"
          }`}
        >
          {gems === 0
            ? "tap a tile to start digging"
            : `CASH OUT · ${mult.toFixed(2)}x (${gems} 💎)`}
        </button>
      )}

      {phase === "busted" && (
        <section className="flex flex-col items-center gap-1">
          <span className="animate-shake text-lg font-bold uppercase tracking-wide text-danger lg:text-2xl">
            💥 boom
          </span>
          <span className="text-sm text-text-muted">
            {gems > 0
              ? `walked away from ${minesMultAfter(gems).toFixed(2)}x — greed tax`
              : "first tap, straight onto a mine. brutal."}
          </span>
        </section>
      )}

      {phase === "cashed" && cashResult && (
        <section className="flex flex-col items-center gap-1">
          <span className="animate-pop font-display text-5xl font-extrabold text-win lg:text-7xl">
            {cashResult.mult.toFixed(2)}x
          </span>
          <span className="text-sm text-text-muted">
            {cashResult.gems} gems · {MINES_COUNT} mines dodged
            {cashResult.gems >= MINES_MAX_PICKS && " · FULL SWEEP"}
          </span>
          {cashResult.total >= 10 && (
            <p className="text-sm text-text">
              #{cashResult.rank} of {cashResult.total}
              <span className="text-accent"> · top {cashResult.percentile}%</span>
            </p>
          )}
        </section>
      )}

      {(phase === "busted" || phase === "cashed") && (
        <>
          {card && (
            <ShareSheet
              card={card}
              replyLink="mines.karrtopelka.com/r"
              caption={
                phase === "cashed"
                  ? `${cashResult?.mult.toFixed(2)}x off the minefield. ${card.provocation}`
                  : `blew up after ${gems} gems. ${card.provocation}`
              }
              loadAssets={loadCardAssets}
            />
          )}
          <button
            type="button"
            onClick={() => {
              setCard(null);
              reset();
            }}
            className="text-sm text-text-muted transition-colors duration-200 hover:text-text"
          >
            new board
          </button>
        </>
      )}

      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          biggest sweeps
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => `${fromScoreValue(v)}x`}
          emptyLabel="minefield's untouched — sweep it."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void launch();
          }}
        />
      )}
    </div>
  );
}
